import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { TrainingType, TrainingInstitute } from '../types';
import { CloseModal } from '../../sites/closemodal';
import type { SelectOption } from '../../../utils/select-with-search';
import { SearchableSelect } from '../../../utils/select-with-search';
import api from '../../../libs/axios';

interface AddCertificationOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
  trainingTypes: TrainingType[];
  institutes: TrainingInstitute[];
  trainingRequestOptions: SelectOption[];
  loading?: boolean;
}

export const AddCertificationOffcanvas: React.FC<AddCertificationOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  trainingTypes,
  institutes,
  trainingRequestOptions,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    training_request_id: '', training_type_id: '', training_institute_id: '',
    certificate_number: '', training_date: '', expiry_date: '',
    training_amount: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SelectOption | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [cardFrontFile, setCardFrontFile] = useState<File | null>(null);
  const [cardBackFile, setCardBackFile] = useState<File | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setShowCloseModal(false);
      setCardFrontFile(null);
      setCardBackFile(null);
      // Auto-select if only one request option is available
      if (trainingRequestOptions.length === 1) {
        const only = trainingRequestOptions[0];
        setSelectedRequest(only);
        setFormData({
          training_request_id: String(only.value),
          training_type_id: '', training_institute_id: '',
          certificate_number: '', training_date: '', expiry_date: '',
          training_amount: '',
        });
      } else {
        setFormData({
          training_request_id: '', training_type_id: '', training_institute_id: '',
          certificate_number: '', training_date: '', expiry_date: '',
          training_amount: '',
        });
        setSelectedRequest(null);
      }
    }
  }, [isOpen]);

  const requestClose = () => {
    if (loading) return;
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.training_request_id) newErrors.training_request_id = 'Required';
    if (!formData.training_type_id) newErrors.training_type_id = 'Required';
    if (!formData.training_institute_id) newErrors.training_institute_id = 'Required';
    if (!formData.training_date) newErrors.training_date = 'Required';
    if (!formData.expiry_date) newErrors.expiry_date = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTrainingRequestSelect = (option: SelectOption | null) => {
    setSelectedRequest(option);
    setFormData(prev => ({ ...prev, training_request_id: option ? String(option.value) : '' }));
    if (errors.training_request_id) {
      setErrors(prev => ({ ...prev, training_request_id: '' }));
    }
  };

  useEffect(() => {
    const requestId = Number(formData.training_request_id);
    if (!isOpen || !requestId || Number.isNaN(requestId)) return;

    let cancelled = false;
    const fetchRequestDetails = async () => {
      setRequestLoading(true);
      try {
        const response = await api.get(`/training/requests/${requestId}`);
        if (!cancelled && response.data?.success) {
          const req = response.data.data?.training_request;
          setFormData(prev => ({
            ...prev,
            training_type_id: req?.training_type_id ? String(req.training_type_id) : prev.training_type_id,
            training_institute_id: req?.training_institute_id ? String(req.training_institute_id) : prev.training_institute_id,
          }));
        }
      } catch (error) {
        console.error("Error fetching training request details:", error);
      } finally {
        if (!cancelled) setRequestLoading(false);
      }
    };

    fetchRequestDetails();
    return () => { cancelled = true; };
  }, [formData.training_request_id, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setUploadingFiles(true);
    try {
      const fd = new FormData();
      fd.append('training_request_id', formData.training_request_id);
      fd.append('training_type_id', formData.training_type_id);
      fd.append('training_institute_id', formData.training_institute_id);
      fd.append('training_date', formData.training_date);
      fd.append('expiry_date', formData.expiry_date);
      if (formData.certificate_number.trim()) fd.append('certificate_number', formData.certificate_number.trim());
      if (formData.training_amount) fd.append('training_amount', formData.training_amount);
      if (cardFrontFile) fd.append('card_front', cardFrontFile);
      if (cardBackFile) fd.append('card_back', cardBackFile);
      await onSubmit(fd);
    } catch (err: any) {
      console.error('Certification submit failed:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create certification');
    } finally {
      setUploadingFiles(false);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading) requestClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? requestClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add Certification</h2>
            <p className="text-sm text-gray-600 mt-1">Record a new training certification.</p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Training Request *</label>
              {/* <input type="number" name="training_request_id" value={formData.training_request_id} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.training_request_id ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="Request ID" /> */}
              <div className={`rounded ${errors.training_request_id ? 'border border-red-300 bg-red-50' : ''}`}>
                <SearchableSelect
                  value={selectedRequest}
                  onChange={handleTrainingRequestSelect}
                  options={trainingRequestOptions}
                  placeholder="Search request IDs"
                  className={`w-full ${loading ? 'pointer-events-none opacity-70' : ''}`}
                  closeOnOutsideClick={false}
                  minSearchLength={3}
                />
              </div>
              {errors.training_request_id && <p className="mt-1 text-sm text-red-600">{errors.training_request_id}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Type *</label>
                <select name="training_type_id" value={formData.training_type_id} onChange={handleInputChange} disabled={loading || requestLoading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white ${errors.training_type_id ? 'border-red-300' : 'border-gray-300'}`}>
                  <option value="">Select type</option>
                  {trainingTypes.map(t => <option key={t.id} value={t.id}>{t.training_name}</option>)}
                </select>
                {errors.training_type_id && <p className="mt-1 text-sm text-red-600">{errors.training_type_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institute *</label>
                <select name="training_institute_id" value={formData.training_institute_id} onChange={handleInputChange} disabled={loading || requestLoading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white ${errors.training_institute_id ? 'border-red-300' : 'border-gray-300'}`}>
                  <option value="">Select institute</option>
                  {institutes.map(i => <option key={i.id} value={i.id}>{i.institute_name}</option>)}
                </select>
                {errors.training_institute_id && <p className="mt-1 text-sm text-red-600">{errors.training_institute_id}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Number</label>
              <input type="text" name="certificate_number" value={formData.certificate_number} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.certificate_number ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="Leave blank to auto-generate" />
              {errors.certificate_number && <p className="mt-1 text-sm text-red-600">{errors.certificate_number}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Date *</label>
                <input type="date" name="training_date" value={formData.training_date} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.training_date ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.training_date && <p className="mt-1 text-sm text-red-600">{errors.training_date}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
                <input type="date" name="expiry_date" value={formData.expiry_date} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.expiry_date ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.expiry_date && <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Training Amount</label>
              <input type="number" name="training_amount" value={formData.training_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="5900.00" step="0.01" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Front Image</label>
              <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm transition-colors ${loading || uploadingFiles ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                <FaUpload className="text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-600">{cardFrontFile ? cardFrontFile.name : 'Click to upload card front image'}</span>
                <input type="file" accept="image/*" className="hidden" disabled={loading || uploadingFiles} onChange={(e) => setCardFrontFile(e.target.files?.[0] || null)} />
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Card Back Image</label>
              <label className={`flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm transition-colors ${loading || uploadingFiles ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                <FaUpload className="text-gray-400 flex-shrink-0" />
                <span className="truncate text-gray-600">{cardBackFile ? cardBackFile.name : 'Click to upload card back image'}</span>
                <input type="file" accept="image/*" className="hidden" disabled={loading || uploadingFiles} onChange={(e) => setCardBackFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={requestClose} disabled={loading || uploadingFiles} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={loading || uploadingFiles} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {(loading || uploadingFiles) ? (<><FaSpinner className="w-4 h-4 animate-spin" />{uploadingFiles ? 'Uploading...' : 'Creating...'}</>) : (<><FaSave className="w-4 h-4" />Create Certification</>)}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};
